import { Button, Popover, Typography } from "@hellocarbo/lasagne";
import { Edit2, MoreHorizontal, Trash } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigation } from "react-router";

import { PortalContainerContext } from "@/contexts";
import { TScenario, TSimulation } from "@/types";
import { formatCarbon } from "@/utils/carbon";
import { cn } from "@/utils/classes";
import { ScenarioIcons } from "@/utils/scenario";

type SimulationItemProps = {
  deleteSimulation: () => void;
  simulation: TSimulation;
};

function SimulationItem({ simulation, deleteSimulation }: SimulationItemProps) {
  const { t } = useTranslation(["formatter", "common"]);
  const { container } = useContext(PortalContainerContext);

  const { formattedCarbon, unit } = formatCarbon(t, simulation.carbon_tons);

  const Icon = simulation.scenario
    ? ScenarioIcons[simulation.scenario]
    : ScenarioIcons[TScenario.free];

  const navigation = useNavigation();

  return (
    <li className="relative">
      <Link
        to={simulation.details_url}
        reloadDocument // allows to leave the router app by reloading the document
        className={cn(
          "border-grayscale-xx-light px-md py-sm rounded-lg border bg-white shadow-sm",
          "hover:border-grayscale-x-light focus-visible:border-grayscale-x-light active:border-grayscale-xx-light hover:shadow-lg focus-visible:shadow-lg active:shadow-sm",
          "grid grid-cols-[3fr_1fr_1fr_1fr]"
        )}
      >
        <div className="gap-x-md flex items-center">
          <Icon className="size-md text-secondary" />
          <div className="gap-y-3xs flex flex-col">
            <Typography as="h2" weight="semibold">
              {simulation.name}
            </Typography>
            <Typography step="-1" weight="semibold" className="text-grayscale">
              {t("formatter:date", {
                value: new Date(simulation.period_start),
                formatParams: {
                  value: { year: "numeric", month: "long", day: "numeric" },
                },
              })}
              &nbsp;-&nbsp;
              {t("formatter:date", {
                value: new Date(simulation.period_end),
                formatParams: {
                  value: { year: "numeric", month: "long", day: "numeric" },
                },
              })}
            </Typography>
          </div>
        </div>
        <Typography
          step="-1"
          weight="semibold"
          className="text-grayscale self-center text-center"
        >
          {t("formatter:number", { value: simulation.employees })}
          &nbsp;
          {t("common:unit.employee", { count: simulation.employees })}
        </Typography>
        <Typography
          step="-1"
          weight="semibold"
          className="text-grayscale self-center text-center whitespace-nowrap"
        >
          {formattedCarbon}
          &nbsp;
          {t(`common:unit.${unit}`)}
        </Typography>
        {/** Fake the Popover trigger to allow the justify-between to work properly */}
        <div className="size-md" />
      </Link>
      <Popover
        trigger={
          <Button
            shape="link"
            Icon={MoreHorizontal}
            className="-right-md mr-md -translate-x-md absolute top-1/2 -translate-y-1/2 justify-self-end"
          />
        }
        portalContainer={container as Element}
        side="left"
        align="start"
      >
        <div className="gap-y-2xs flex flex-col">
          <Button
            as={Link}
            to={`/simulations/${simulation.id}/parameters`}
            shape="link"
            size="small"
            className="text-left"
          >
            <Edit2 className="size-sm" />
            {t("common:modify")}
          </Button>
          <Button
            shape="link"
            intent="negative"
            size="small"
            className="text-left"
            onClick={deleteSimulation}
            isLoading={navigation.state === "submitting"}
          >
            <Trash className="size-sm" />
            {t("common:delete")}
          </Button>
        </div>
      </Popover>
    </li>
  );
}

export default SimulationItem;
